import React from 'react';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import FlightIcon from '@mui/icons-material/Flight';
import BusinessIcon from '@mui/icons-material/Business'
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DirectionsBoatFilledIcon from '@mui/icons-material/DirectionsBoatFilled';
import CheckIcon from '@mui/icons-material/Check';
import GridOnIcon from '@mui/icons-material/GridOn';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { Alert, Box, Button, Chip, Collapse, FormLabel, Modal, Typography } from '@mui/material';
import { Autocomplete, InputAdornment, TextField, Tooltip } from '@mui/material';
import User from '../../../../Models/Interfaces/User';
import Address from '../../../../Models/Interfaces/Address';
import Certificate from '../../../../Models/Interfaces/Certificate';
import ActionTypes from '../../../../Constants/ActionTypes';
import { AuthService } from '../../../../Services/AuthService';
import CertificateSubmit from '../../../../Models/SubmitModels/CertificateSubmit';
import TransportMethods from '../../../../Constants/TransportMethods';
import CertificateTypes from '../../../../Constants/CertificateTypes';
import { CertificateErrorModel } from '../../../../Models/ErrorModels/CertificateErrorModel';
import { CertificateModalWrapper, InputFieldContainer, InputRow, NewCertificateTitle, StyledBox } from './CertificateModal.styled';
import { createCertificate, editCertificate } from '../../../../Services/VeidivottordAPIService';
import ReferenceData from '../../../../Models/Interfaces/ReferenceData';
import { Edit } from '@mui/icons-material';
import BlockIcon from '@mui/icons-material/Block';
import UnsavedChangesDialog from '../../../../SharedComponents/UnsavedChangesDialog';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';

const EU_EEA_CERTIFICATE_TYPE_ID = 2;
const EU_EEA_ALLOWED_COUNTRY_CODES = [
  "SC", "GB", "TW", "CN", "TH", "NO", "AR", "NZ", "MY", "CR", "MU", "FK", "EC", "BS", "PF", "TR",
  "FJ", "GT", "SB", "CA", "MV", "NI", "KE", "GM", "VE", "PH", "IN", "BD", "GN", "PK", "OM", "MG",
  "GH", "BJ", "AE", "CI", "PE", "VN", "NA", "MZ", "DZ", "CO", "TZ", "BR", "GA", "SR", "YE", "SV",
  "RU", "UY", "FO", "IS", "LK", "TN", "GY", "US", "JP", "TF", "BZ", "PG", "NC", "AG", "MA", "GL",
  "ME", "MX", "CU", "NG", "EG", "CL", "JM", "AL", "GD", "SN", "ID", "ZA", "MR", "CV", "PA", "AU",
  "KR", "UA", "AO", "CH", "LI", "AT", "BE", "BG", "CY", "CZ", "DE", "DK", "EE", "ES", "FI", "FR",
  "GR", "HR", "HU", "IE", "IT", "LT", "LU", "LV", "MT", "NL", "PL", "PT", "RO", "SE", "SI", "SK", "XI", "AZ", "GE", "BY"
];
const EU_EEA_COUNTRY_CODE_SET = new Set(EU_EEA_ALLOWED_COUNTRY_CODES);
const IMO_NUMBER_REGEX = /^\d{7}$/;
const hasValidImoCheckDigit = (imoNumber: string) => {
  if (!IMO_NUMBER_REGEX.test(imoNumber)) {
    return false;
  }

  const weightedSum = imoNumber
    .slice(0, 6)
    .split('')
    .reduce((sum, digit, index) => sum + Number(digit) * (7 - index), 0);

  return weightedSum % 10 === Number(imoNumber[6]);
};

interface NewCertificateModalProps {
  toggleCertificateModal: () => void;
  certificateModalOpen: boolean;
  openSnackbar: (statusCode: number, message?: string) => void;
  setLoadingState: (loadingState: boolean) => void;
  fetchCertificate: (certificateId: number) => Promise<unknown>;
  fetchCertificates: (fromDate?: Date, toDate?: Date) => Promise<void>;
  modalActionType: string | undefined;
  inputToFocus: string | undefined;
  selectedCertificate: Certificate | null;
  windowDimensions: { width: number, height: number };
  userInfo: User | null;
  certificates: Certificate[] | null;
  receiverAddresses: Address[];
  exporterAddresses: Address[];
  fromDate: Date | undefined;
  toDate: Date | undefined;
  referenceData: ReferenceData | undefined;
}

const CertificateModal: FC<NewCertificateModalProps> = (props) => {

  const [transportMethod, setTransportMethod] = useState<string | undefined>(TransportMethods.SHIP);
  const [certificateFormData, setCertificateFormData] = useState<CertificateSubmit>({} as CertificateSubmit);
  const formInitialized = useRef(false);
  const initialFormData = useRef<CertificateSubmit>({} as CertificateSubmit);
  const [initialCertificate, setInitialCertificate] = useState<Certificate | null>(null);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [errorModel, setErrorModel] = useState<CertificateErrorModel>({} as CertificateErrorModel);
  const [currentUserCompanyName, setCurrentUserCompanyName] = useState<string | undefined>('');
  const [receiverModalOpen, setReceiverModalOpen] = useState<boolean>(false);
  const [exporterModalOpen, setExporterModalOpen] = useState<boolean>(false);
  const [containerInput, setContainerInput] = useState<string>('');
  const [containerList, setContainerList] = useState<string[]>([]);
  const [containerSealMap, setContainerSealMap] = useState<Record<string, string>>({});
  const [containerMode, setContainerMode] = useState<'containers' | 'no-containers' | null>(null);
  const [showRoadVehicleNumber, setShowRoadVehicleNumber] = useState(false);
  const [showReceiverEORI, setShowReceiverEORI] = useState(false);
  const [showExporterEORI, setShowExporterEORI] = useState(false);

  useEffect(() => {
    if (certificateFormData?.receiverEORI) {
      setShowReceiverEORI(true);
    }
  }, [certificateFormData?.receiverEORI]);

  useEffect(() => {
    if (certificateFormData?.exporterEORI) {
      setShowExporterEORI(true);
    }
  }, [certificateFormData?.exporterEORI]);

  const REFERENCE_NUMBER_MAX_LENGTH = 20;
  const TRANSPORTING_SHIP_MAX_LENGTH = 100;
  const VOYAGE_NUMBER_MAX_LENGTH = 100;
  const FLIGHT_NUMBER_MAX_LENGTH = 100;
  const CONTAINER_NUMBER_MAX_LENGTH = 500;
  const TRACKING_NUMBER_MAX_LENGTH = 100;
  const ROAD_VEHICLE_NUMBER_MAX_LENGTH = 100;
  const RECEIVER_NAME_MAX_LENGTH = 50;
  const RECEIVER_ADDRESS_MAX_LENGTH = 500;
  const EXPORTER_NAME_MAX_LENGTH = 50;
  const EXPORTER_ADDRESS_MAX_LENGTH = 500;
  const CONTAINER_REGEX = /^[A-Z]{3}[UJZR]\d{7}$/;
  const CONTAINER_FORMAT_HELP = 'ISO 6346 snið: AAAU1234567. Stafur í sæti 4 verður að vera U, J, Z eða R og síðustu 7 eru tölur. Sláðu inn eitt númer í einu.';
  const EU_TRANSPORT_REQUIRED_MESSAGE = 'Fyrir EU þarf að fylla inn nafn flutningaskips eða flugnúmer.';

  const shipValue = certificateFormData?.transportingShip || '';
  const flightValue = certificateFormData?.flightNumber || '';
  const isEU = certificateFormData?.typeId === CertificateTypes.EU;
  const requiresContainerRegex = isEU;
  const missingShip = isEU && transportMethod === TransportMethods.SHIP && shipValue.trim().length === 0;
  const missingFlight = isEU && transportMethod === TransportMethods.AIRPLANE && flightValue.trim().length === 0;

  const euEeaTypeIds = useMemo(
    () => Array.from(new Set([CertificateTypes.EU, EU_EEA_CERTIFICATE_TYPE_ID])),
    []
  );
  const isEUEEACertificate = certificateFormData?.typeId !== undefined && euEeaTypeIds.includes(certificateFormData.typeId);

  const countryCodes = props.referenceData?.countryCodes || [];

  const filteredCountryCodes = useMemo(() => {
    if (!isEUEEACertificate) {
      return countryCodes;
    }
    return countryCodes.filter(country => EU_EEA_COUNTRY_CODE_SET.has(country.countryCode));
  }, [countryCodes, isEUEEACertificate]);

  const receiverEuCountryOptions = useMemo(
    () => filteredCountryCodes.filter(country => country.countryCode !== 'US'),
    [filteredCountryCodes]
  );

  const icelandCountry = useMemo(
    () => countryCodes.find(country => country.countryCode === 'IS'),
    [countryCodes]
  );

  const usaCountry = useMemo(
    () => countryCodes.find(country => country.countryCode === 'US'),
    [countryCodes]
  );

  const japanCountry = useMemo(
    () => countryCodes.find(country => country.countryCode === 'JP'),
    [countryCodes]
  );

  const receiverCountryOptions = useMemo(() => {
    if (certificateFormData?.typeId === CertificateTypes.USA) {
      return usaCountry ? [usaCountry] : [];
    }
    if (certificateFormData?.typeId === CertificateTypes.JAPAN) {

      // Temporary change to only allow EU countries for Japan certificates.
      return receiverEuCountryOptions;
      //return japanCountry ? [japanCountry] : [];
    }
    if (certificateFormData?.typeId === CertificateTypes.EU) {
      return receiverEuCountryOptions;
    }
    return countryCodes;
  }, [
    certificateFormData?.typeId,
    countryCodes,
    receiverEuCountryOptions,
    usaCountry,
    japanCountry
  ]);

  const findCountryById = (countryCodeId?: number | null) => countryCodes.find(country => country.id === countryCodeId);
  const exporterCountry = findCountryById(certificateFormData?.exporterCountryCodeId);
  const exporterCountryInvalid = isEUEEACertificate
    && certificateFormData?.exporterCountryCodeId !== undefined
    && certificateFormData?.exporterCountryCodeId !== null
    && (!exporterCountry || !EU_EEA_COUNTRY_CODE_SET.has(exporterCountry.countryCode));
  const hasInvalidEuCountryCodes = exporterCountryInvalid;

  useEffect(() => {
    if (!icelandCountry?.id) {
      return;
    }
    if (certificateFormData?.exporterCountryCodeId !== icelandCountry.id) {
      handleChange("exporterCountryCodeId", icelandCountry.id);
    }
  }, [certificateFormData?.exporterCountryCodeId, icelandCountry?.id]);

  useEffect(() => {
    if (certificateFormData?.typeId === CertificateTypes.USA && usaCountry?.id) {
      if (certificateFormData?.receiverCountryCodeId !== usaCountry.id) {
        handleChange("receiverCountryCodeId", usaCountry.id);
      }
      return;
    }
    if (certificateFormData?.typeId === CertificateTypes.JAPAN && japanCountry?.id) {
      // Temporary change to only allow EU countries for Japan certificates.
      //if (certificateFormData?.receiverCountryCodeId !== japanCountry.id) {
      //  handleChange("receiverCountryCodeId", japanCountry.id);
      //}
      return;
    }
    if (certificateFormData?.typeId === CertificateTypes.EU) {
      const receiverCountry = findCountryById(certificateFormData?.receiverCountryCodeId);
      const receiverCountryAllowed = receiverCountryOptions.some(option => option.id === receiverCountry?.id);
      if (receiverCountry && !receiverCountryAllowed) {
        handleChange("receiverCountryCodeId", null);
      }
    }
  }, [
    certificateFormData?.typeId,
    certificateFormData?.receiverCountryCodeId,
    usaCountry?.id,
    japanCountry?.id,
    receiverCountryOptions
  ]);

  const isBlank = (value?: string | number | null) => {
    if (value === undefined || value === null) {
      return true;
    }
    return value.toString().trim().length === 0;
  };

  const handleChange = (name: string, value: string | number | null | undefined) => {
    setCertificateFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
  };

  const buildInvalidContainerMessage = (invalidValues: string[]) => {
    const listed = invalidValues.slice(0, 3).join(', ');
    return `Ógild gámanúmer (${invalidValues.length}): ${listed}. ${CONTAINER_FORMAT_HELP}`;
  };

  const buildContainerSealValue = (list: string[], sealMap: Record<string, string>) => {
    const sealValues = list.map(container => (sealMap[container] || '').trim().toUpperCase());
    return sealValues.some(Boolean) ? sealValues.join(';') : null;
  };

  const validateAndPersistContainers = (list: string[], sealMap: Record<string, string> = containerSealMap) => {
    const invalidContainers = requiresContainerRegex
      ? list.filter(value => !CONTAINER_REGEX.test(value))
      : [];
    const containersToPersist = requiresContainerRegex
      ? list.filter(value => CONTAINER_REGEX.test(value))
      : list;
    const joinedContainers = containersToPersist.join(';');
    const joinedSeals = buildContainerSealValue(containersToPersist, sealMap);
    const exceedsMaxLength = joinedContainers.length > CONTAINER_NUMBER_MAX_LENGTH;

    setErrorModel(prev => ({
      ...prev,
      containerNumber: {
        error: invalidContainers.length > 0 || exceedsMaxLength,
        message: invalidContainers.length > 0
          ? buildInvalidContainerMessage(invalidContainers)
          : exceedsMaxLength
            ? `Heildarlengd (${joinedContainers.length}) fer yfir ${CONTAINER_NUMBER_MAX_LENGTH} stafi. Minnka fj??lda g??ma.`
            : '',
      }
    }));

    handleChange("containerNumber", joinedContainers);
    handleChange("containerSealNumber", joinedSeals);
  };

  useEffect(() => {
    if (transportMethod !== TransportMethods.SHIP) {
      return;
    }
    validateAndPersistContainers(containerList);
  }, [requiresContainerRegex]);


  const updateContainerList = (updater: ((prev: string[]) => string[]) | string[]) => {
    const nextList = typeof updater === 'function' ? updater(containerList) : updater;
    const nextSealMap = nextList.reduce<Record<string, string>>((acc, container) => {
      acc[container] = containerSealMap[container] || '';
      return acc;
    }, {});

    setContainerList(nextList);
    setContainerSealMap(nextSealMap);
    validateAndPersistContainers(nextList, nextSealMap);
  };

  const parseContainerInput = (value: string) => {
    const trimmed = value.trim().toUpperCase();
    return trimmed ? [trimmed] : [];
  };

  const addContainersFromInput = () => {
    const parts = parseContainerInput(containerInput);
    if (!parts.length) {
      setContainerInput(containerInput.toUpperCase());
      return;
    }

    const invalidParts = requiresContainerRegex
      ? parts.filter(part => !CONTAINER_REGEX.test(part))
      : [];
    if (invalidParts.length > 0) {
      setErrorModel(prev => ({
        ...prev,
        containerNumber: {
          error: true,
          message: buildInvalidContainerMessage(invalidParts)
        }
      }));
      return;
    }

    updateContainerList(prevList => {
      const merged = [...prevList];
      parts.forEach(part => {
        if (!merged.includes(part)) {
          merged.push(part);
        }
      });
      return merged;
    });

    setErrorModel(prev => ({
      ...prev,
      containerNumber: { error: false, message: '' }
    }));
    setContainerInput('');
  };

  const handleRemoveContainer = (container: string) => {
    updateContainerList(prevList => prevList.filter(item => item !== container));
  };

  const handleContainerSealChange = (container: string, value: string) => {
    const upperValue = value.toUpperCase();
    setContainerSealMap(prevSealMap => {
      const nextSealMap = { ...prevSealMap, [container]: upperValue };
      validateAndPersistContainers(containerList, nextSealMap);
      return nextSealMap;
    });
  };

  const handleContainerInputChange = (value: string) => {
    const upperValue = value.toUpperCase();
    setContainerInput(upperValue);
    validateAndPersistContainers(containerList);
  };

  const getCurrentUser = async () => {
    const authService = new AuthService();
    const user = await authService.getUser();
    setCurrentUserCompanyName(user?.profile?.name);
  }

  useEffect(() => {
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (!props.certificateModalOpen) {
      formInitialized.current = false;
      return;
    }
    if (formInitialized.current) {
      return;
    }
    if (props.modalActionType === ActionTypes.EDIT_ACTION && !props.selectedCertificate?.id) {
      return;
    }

    // Upphafsgildi eru aðeins sótt við opnun svo endurhleðsla yfirskrifi ekki innslátt.
    formInitialized.current = true;
    setInitialCertificate(props.selectedCertificate);
    let formData: CertificateSubmit;
    if (props.selectedCertificate !== null && props.modalActionType === ActionTypes.EDIT_ACTION) {
      const certificate = props.selectedCertificate;
      formData = {
        referenceNumber: certificate.referenceNumber,
        transportingShip: certificate.transportingShip,
        transportingShipImoNumber: certificate.transportingShipImoNumber,
        voyageNumber: certificate.voyageNumber,
        flightNumber: certificate.flightNumber,
        roadVehicleNumber: certificate.roadVehicleNumber,
        containerNumber: certificate.containerNumber,
        containerSealNumber: certificate.containerSealNumber,
        trackingNumber: certificate.trackingNumber,
        exporterName: certificate.exporterName,
        receiverName: certificate.receiverName,
        receiverCity: certificate.receiverCity,
        receiverPostalCode: certificate.receiverPostalCode,
        exporterAddress: certificate.exporterAddress,
        exporterCountryCodeId: certificate.exporterCountryCodeId,
        exporterCity: certificate.exporterCity,
        exporterPostalCode: certificate.exporterPostalCode,
        exporterEORI: certificate.exporterEORI,
        receiverAddress: certificate.receiverAddress,
        receiverCountryCodeId: certificate.receiverCountryCodeId,
        receiverEORI: certificate.receiverEORI,
        typeId: certificate.typeId,
      };
    } else {
      formData = {
        exporterAddress: props.exporterAddresses.length ? props.exporterAddresses[0].streetName : "",
        exporterName: props.exporterAddresses.length ? props.exporterAddresses[0].subjectName : "",
        exporterCountryCodeId: props.exporterAddresses.length ? props.referenceData?.countryCodes.find(code => code.countryCode === props.exporterAddresses[0].countryCode)?.id : undefined,
        exporterCity: props.exporterAddresses.length ? props.exporterAddresses[0].cityName : "",
        exporterPostalCode: props.exporterAddresses.length ? props.exporterAddresses[0].postalCode : "",
        typeId: props.certificates?.length ? props.certificates[0].typeId : CertificateTypes.EU,
      } as CertificateSubmit;
    }
    initialFormData.current = formData;
    setCertificateFormData(formData);
    setErrorModel({} as CertificateErrorModel);
    setDiscardDialogOpen(false);
    setSaveError('');
    setContainerInput('');

    const previousCertificate = props.modalActionType === ActionTypes.EDIT_ACTION
      ? props.selectedCertificate
      : props.certificates?.[0];
    setTransportMethod(previousCertificate?.flightNumber ? TransportMethods.AIRPLANE : TransportMethods.SHIP);
  }, [props.certificateModalOpen, props.selectedCertificate, props.modalActionType, props.certificates, props.exporterAddresses, props.referenceData]);

  useEffect(() => {
    if (!props.certificateModalOpen) {
      return;
    }
    if (transportMethod !== TransportMethods.SHIP) {
      updateContainerList([]);
      setContainerInput('');
      setContainerSealMap({});
      setContainerMode(null);
      setShowRoadVehicleNumber(false);
      return;
    }

    if (props.modalActionType === ActionTypes.EDIT_ACTION && initialCertificate?.containerNumber) {
      const parsedContainers = initialCertificate.containerNumber
        .split(/[,;\n\r]+/)
        .map(container => container.trim().toUpperCase())
        .filter(Boolean);
      const parsedSeals = initialCertificate.containerSealNumber
        ?.split(';')
        .map(seal => seal.trim().toUpperCase()) ?? [];
      const parsedSealMap = parsedContainers.reduce<Record<string, string>>((acc, container, index) => {
        acc[container] = parsedSeals[index] || '';
        return acc;
      }, {});

      setContainerSealMap(parsedSealMap);
      setContainerList(parsedContainers);
      validateAndPersistContainers(parsedContainers, parsedSealMap);
      setContainerMode(parsedContainers.length > 0 ? 'containers' : 'no-containers');
      setShowRoadVehicleNumber(Boolean(initialCertificate?.roadVehicleNumber?.trim()));
    } else if (props.modalActionType === ActionTypes.EDIT_ACTION) {
      updateContainerList([]);
      setContainerInput('');
      setContainerSealMap({});
      setContainerMode('no-containers');
      setShowRoadVehicleNumber(Boolean(initialCertificate?.roadVehicleNumber?.trim()));
    } else if (props.modalActionType === ActionTypes.CREATE_ACTION) {
      updateContainerList([]);
      setContainerInput('');
      setContainerSealMap({});
      setContainerMode(null);
      setShowRoadVehicleNumber(false);
    }
  }, [props.certificateModalOpen, initialCertificate, props.modalActionType, transportMethod]);

  const handleSelectContainerMode = (mode: 'containers' | 'no-containers') => {
    setContainerMode(mode);
    if (mode === 'no-containers') {
      updateContainerList([]);
      setContainerInput('');
      setContainerSealMap({});
      handleChange("containerNumber", null);
      handleChange("containerSealNumber", null);
    }
  };

  useEffect(() => {
    const receiverCityMissing = isBlank(certificateFormData?.receiverCity);
    const exporterCityMissing = isBlank(certificateFormData?.exporterCity);
    const receiverPostalMissing = isBlank(certificateFormData?.receiverPostalCode);
    const exporterPostalMissing = isBlank(certificateFormData?.exporterPostalCode);

    setErrorModel(prev => ({
      ...prev,
      receiverCity: {
        error: receiverCityMissing,
        message: receiverCityMissing ? 'Borg er skylda.' : '',
      },
      exporterCity: {
        error: exporterCityMissing,
        message: exporterCityMissing ? 'Borg er skylda.' : '',
      },
      receiverPostalCode: {
        error: receiverPostalMissing,
        message: receiverPostalMissing ? 'Póstnúmer er skylt.' : '',
      },
      exporterPostalCode: {
        error: exporterPostalMissing,
        message: exporterPostalMissing ? 'Póstnúmer er skylt.' : '',
      },
    }));
  }, [certificateFormData?.receiverCity, certificateFormData?.exporterCity, certificateFormData?.receiverPostalCode, certificateFormData?.exporterPostalCode]);

  useEffect(() => {
    const shipValue = certificateFormData?.transportingShip || '';
    const imoNumber = certificateFormData?.transportingShipImoNumber || '';
    const voyageNumber = certificateFormData?.voyageNumber || '';
    const flightValue = certificateFormData?.flightNumber || '';
    const shipLengthError = shipValue.length > TRANSPORTING_SHIP_MAX_LENGTH;
    const imoNumberFormatError = imoNumber.length > 0 && !IMO_NUMBER_REGEX.test(imoNumber);
    const imoNumberCheckDigitError = imoNumber.length > 0 && !imoNumberFormatError && !hasValidImoCheckDigit(imoNumber);
    const imoNumberError = imoNumberFormatError || imoNumberCheckDigitError;
    const voyageNumberError = voyageNumber.length > VOYAGE_NUMBER_MAX_LENGTH;
    const flightLengthError = flightValue.length > FLIGHT_NUMBER_MAX_LENGTH;

    setErrorModel(prev => ({
      ...prev,
      transportingShip: {
        error: shipLengthError,
        message: shipLengthError
          ? `Nafn flutningaskips má ekki vera lengra en ${TRANSPORTING_SHIP_MAX_LENGTH} stafir.`
          : '',
      },
      transportingShipImoNumber: {
        error: imoNumberError,
        message: imoNumberFormatError
          ? 'IMO-númer verður að vera 7 tölustafir.'
          : imoNumberCheckDigitError
            ? 'IMO-númer er ekki gilt. Athugaðu síðasta tölustafinn.'
            : '',
      },
      voyageNumber: {
        error: voyageNumberError,
        message: voyageNumberError
          ? `Ferðanúmer má ekki vera lengra en ${VOYAGE_NUMBER_MAX_LENGTH} stafir.`
          : '',
      },
      flightNumber: {
        error: flightLengthError,
        message: flightLengthError
          ? `Flugnúmer má ekki vera lengra en ${FLIGHT_NUMBER_MAX_LENGTH} stafir.`
          : '',
      },
    }));
  }, [certificateFormData?.typeId, transportMethod, certificateFormData?.transportingShip, certificateFormData?.transportingShipImoNumber, certificateFormData?.voyageNumber, certificateFormData?.flightNumber]);

  const handleSelectTransportMethod = (transportMethod: string) => {
    setTransportMethod(transportMethod);
    if (transportMethod === TransportMethods.SHIP) {
      handleChange("flightNumber", null);
      setContainerMode(null);
    } else {
      handleChange("containerNumber", null);
      handleChange("containerSealNumber", null);
      updateContainerList([]);
      setContainerInput('');
      setContainerSealMap({});
      setShowRoadVehicleNumber(false);
      handleChange("transportingShip", null);
      handleChange("transportingShipImoNumber", null);
      handleChange("voyageNumber", null);
      handleChange("roadVehicleNumber", null);
    }
  }

  const ensureCitiesFilled = () => {
    const receiverCityMissing = isBlank(certificateFormData?.receiverCity);
    const exporterCityMissing = isBlank(certificateFormData?.exporterCity);
    const receiverPostalMissing = isBlank(certificateFormData?.receiverPostalCode);
    const exporterPostalMissing = isBlank(certificateFormData?.exporterPostalCode);

    if (!receiverCityMissing && !exporterCityMissing && !receiverPostalMissing && !exporterPostalMissing) {
      return false;
    }

    setErrorModel(prev => ({
      ...prev,
      receiverCity: {
        error: receiverCityMissing,
        message: receiverCityMissing ? 'Borg er skylda.' : '',
      },
      exporterCity: {
        error: exporterCityMissing,
        message: exporterCityMissing ? 'Borg er skylda.' : '',
      },
      receiverPostalCode: {
        error: receiverPostalMissing,
        message: receiverPostalMissing ? 'Póstnúmer er skylt.' : '',
      },
      exporterPostalCode: {
        error: exporterPostalMissing,
        message: exporterPostalMissing ? 'Póstnúmer er skylt.' : '',
      },
    }));

    return true;
  };

  const handleSubmitCertificate = async () => {
    if (isSaving || ensureCitiesFilled() || hasInvalidEuCountryCodes) {
      return;
    }

    setIsSaving(true);
    setSaveError('');
    try {
      const response = await createCertificate(props.setLoadingState, certificateFormData);
      try {
        await props.fetchCertificates(props.fromDate, props.toDate);
        await props.fetchCertificate(response.data);
        props.openSnackbar(response.status);
      } catch {
        props.openSnackbar(500, 'Vottorðið var vistað en ekki tókst að endurhlaða því. Endurhladdu síðuna.');
      }
      props.toggleCertificateModal();
    } catch (e) {
      setSaveError('Ekki tókst að vista vottorðið. Upplýsingarnar eru enn í glugganum. Reyndu aftur.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditCertificate = async () => {
    if (isSaving || ensureCitiesFilled() || hasInvalidEuCountryCodes || !initialCertificate?.id) {
      return;
    }

    setIsSaving(true);
    setSaveError('');
    try {
      const response = await editCertificate(props.setLoadingState, certificateFormData, initialCertificate.id);
      try {
        await props.fetchCertificate(response.data);
        props.openSnackbar(response.status);
      } catch {
        props.openSnackbar(500, 'Breytingarnar voru vistaðar en ekki tókst að endurhlaða vottorðinu. Endurhladdu síðuna.');
      }
      props.toggleCertificateModal();
    } catch (e) {
      setSaveError('Ekki tókst að vista breytingarnar. Upplýsingarnar eru enn í glugganum. Reyndu aftur.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (isSaving) {
      return;
    }
    const fields = Object.keys({ ...initialFormData.current, ...certificateFormData }) as (keyof CertificateSubmit)[];
    const hasUnsavedChanges = fields.some(field =>
      (certificateFormData[field] ?? '') !== (initialFormData.current[field] ?? '')
    ) || containerInput.trim().length > 0;

    if (hasUnsavedChanges) {
      setDiscardDialogOpen(true);
    } else {
      props.toggleCertificateModal();
    }
  };

  const pendingContainers = parseContainerInput(containerInput);
  const canAddContainers = pendingContainers.length > 0 && (
    requiresContainerRegex
      ? pendingContainers.every(part => CONTAINER_REGEX.test(part))
      : true
  );
  const submitRequirementsNotMet = !Object.values(errorModel).every(error => error.error === false) ||
    certificateFormData?.receiverAddress === undefined ||
    certificateFormData?.receiverName === undefined ||
    isBlank(certificateFormData?.receiverCity) ||
    isBlank(certificateFormData?.receiverPostalCode) ||
    certificateFormData?.exporterAddress === undefined ||
    certificateFormData?.exporterName === undefined ||
    isBlank(certificateFormData?.exporterCity) ||
    isBlank(certificateFormData?.exporterPostalCode) ||
    certificateFormData?.receiverCountryCodeId == null ||
    certificateFormData?.exporterCountryCodeId == null ||
    certificateFormData?.typeId === undefined ||
    (transportMethod === TransportMethods.SHIP && containerMode === null) ||
    (transportMethod === TransportMethods.SHIP && containerMode === 'containers' && containerList.length === 0) ||
    missingShip ||
    missingFlight;
  const isSubmitDisabled = isSaving || !formInitialized.current || submitRequirementsNotMet || hasInvalidEuCountryCodes;
  const submitTooltip = hasInvalidEuCountryCodes
    ? 'Landskodi verdur ad vera a leyfdum lista fyrir EU/EES.'
    : missingShip || missingFlight
      ? EU_TRANSPORT_REQUIRED_MESSAGE
      : transportMethod === TransportMethods.SHIP && containerMode === null
        ? 'Veldu gámaflutning eða engir gámar.'
        : transportMethod === TransportMethods.SHIP && containerMode === 'containers' && containerList.length === 0
          ? 'Skráðu að minnsta kosti eitt gámanúmer.'
          : submitRequirementsNotMet
            ? 'Upplýsingar móttakanda/útflytjanda þurfa að vera fylltar út.'
            : '';

  function normalizeEori(input: string) {
    return (input || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  }

  function looksLikeEori(eori: string) {
    const x = normalizeEori(eori);
    // Rough format check: CC + 1..15 alnum (total 3..17)
    return /^[A-Z]{2}[A-Z0-9]{1,15}$/.test(x);
  }


  return (
    <CertificateModalWrapper>
      <Modal
        open={props.certificateModalOpen}
        onClose={handleClose}
      >
        <StyledBox aria-busy={isSaving}>
          <Box
            component="fieldset"
            disabled={isSaving}
            onKeyDownCapture={event => {
              if (isSaving) {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            sx={{ border: 0, p: 0, m: 0, minWidth: 0, pointerEvents: isSaving ? 'none' : 'auto' }}
          >

          <NewCertificateTitle>
            {props.modalActionType === ActionTypes.EDIT_ACTION ? "Breyta veiðivottorði" : "Nýtt veiðivottorð"}
          </NewCertificateTitle>

          <InputFieldContainer>
            <Box>
              <FormLabel
                sx={{
                  fontSize: 14,
                  color: 'text.primary',
                  fontWeight: 600,
                  letterSpacing: 0.2,
                  borderBottom: '2px solid #1976d2',
                  pb: 0.5,
                  mb: 1,
                  display: 'inline-block'
                }}
              >
                Áfangastaður / tilvísunarnúmer
              </FormLabel>
            </Box>

            <InputRow>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  width: '100%',
                  justifyContent: 'stretch',
                  mt: 0.5,
                  mb: 0.5,
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                }}
              >
                {/* EU / EES */}
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.2,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    boxShadow: certificateFormData?.typeId === CertificateTypes.EU ? '0 1px 4px rgba(25,118,210,0.10)' : 'none',
                    background:
                      certificateFormData?.typeId === CertificateTypes.EU
                        ? 'linear-gradient(180deg, #f5faff 0%, #f0f7ff 100%)'
                        : 'background.paper',
                    fontWeight: certificateFormData?.typeId === CertificateTypes.EU ? 600 : 500,
                    fontSize: '0.95rem',
                    minHeight: 40,
                    outline: 'none',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: '0 1px 6px rgba(25,118,210,0.13)',
                      transform: 'translateY(-1px)',
                    },
                    '&:focus-visible': {
                      borderColor: 'primary.main',
                      boxShadow: '0 1px 6px rgba(25,118,210,0.13)',
                      transform: 'translateY(-1px)',
                    }
                  }}
                  onClick={() => handleChange('typeId', CertificateTypes.EU)}
                  tabIndex={0}
                  role="button"
                  aria-label="Veldu EU/EES"
                  aria-pressed={certificateFormData?.typeId === CertificateTypes.EU}
                  onKeyDown={e => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault();
                      handleChange('typeId', CertificateTypes.EU);
                    }
                  }}
                >
                  <img
                    src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANBAMAAABbflNtAAAAMFBMVEUAM5kiR4MIOJKqmi0tTnoZQogGNpRyeU8LOpFGXWyvnivCqSEBM5dpcVOXjjibkDZC0N4wAAAASUlEQVQI12NgQAUcDTAWl/hFAygztIEjFMLiBAoxTwAz2UBEAph5WoGBgWkPmkkMLB8YGPgcIKYqMjAIKUDNdS+BmctwUABNDwCX4AkbzIMR+gAAAABJRU5ErkJggg=="
                    width="22"
                    alt="EU / EES"
                    style={{ marginRight: '0.5em', filter: 'grayscale(10%)' }}
                  />
                  <span style={{ flex: 1, lineHeight: 1.15, fontSize: '0.95rem' }}>EU / EES</span>
                  {certificateFormData?.typeId === CertificateTypes.EU && (
                    <CheckIcon sx={{ ml: 1, color: 'primary.main', fontSize: 18 }} />
                  )}
                </Box>
                {/* USA */}
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.2,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    boxShadow: certificateFormData?.typeId === CertificateTypes.USA ? '0 1px 4px rgba(25,118,210,0.10)' : 'none',
                    background:
                      certificateFormData?.typeId === CertificateTypes.USA
                        ? 'linear-gradient(180deg, #f5faff 0%, #f0f7ff 100%)'
                        : 'background.paper',
                    fontWeight: certificateFormData?.typeId === CertificateTypes.USA ? 600 : 500,
                    fontSize: '0.95rem',
                    minHeight: 40,
                    outline: 'none',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: '0 1px 6px rgba(25,118,210,0.13)',
                      transform: 'translateY(-1px)',
                    },
                    '&:focus-visible': {
                      borderColor: 'primary.main',
                      boxShadow: '0 1px 6px rgba(25,118,210,0.13)',
                      transform: 'translateY(-1px)',
                    }
                  }}
                  onClick={() => handleChange('typeId', CertificateTypes.USA)}
                  tabIndex={0}
                  role="button"
                  aria-label="Veldu USA"
                  aria-pressed={certificateFormData?.typeId === CertificateTypes.USA}
                  onKeyDown={e => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault();
                      handleChange('typeId', CertificateTypes.USA);
                    }
                  }}
                >
                  <img
                    src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAALBAMAAACNJ7BwAAAAMFBMVEXovcLBS1rQd4LUg43jsLZSTnzirbTPdIDEVWNeXYfVhY9lZIxKRHNWVYHUhI7jsLe2+MkaAAAARUlEQVQI12OIORpzVBAMGKJ3zt6aBgZg5qtVQLCOIfbq3avlYMAwc+bOmQwQANLWAQYMSEAJDhgeG8MAwxcXKHBjEIQDADRVI7/3XjC6AAAAAElFTkSuQmCC"
                    width="22"
                    alt="USA"
                    style={{ marginRight: '0.5em', filter: 'grayscale(10%)' }}
                  />
                  <span style={{ flex: 1, lineHeight: 1.15, fontSize: '0.95rem' }}>USA</span>
                  {certificateFormData?.typeId === CertificateTypes.USA && (
                    <CheckIcon sx={{ ml: 1, color: 'primary.main', fontSize: 18 }} />
                  )}
                </Box>
                {/* Japan */}
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.2,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    boxShadow: certificateFormData?.typeId === CertificateTypes.JAPAN ? '0 1px 4px rgba(25,118,210,0.10)' : 'none',
                    background:
                      certificateFormData?.typeId === CertificateTypes.JAPAN
                        ? 'linear-gradient(180deg, #f5faff 0%, #f0f7ff 100%)'
                        : 'background.paper',
                    fontWeight: certificateFormData?.typeId === CertificateTypes.JAPAN ? 600 : 500,
                    fontSize: '0.95rem',
                    minHeight: 40,
                    outline: 'none',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: '0 1px 6px rgba(25,118,210,0.13)',
                      transform: 'translateY(-1px)',
                    },
                    '&:focus-visible': {
                      borderColor: 'primary.main',
                      boxShadow: '0 1px 6px rgba(25,118,210,0.13)',
                      transform: 'translateY(-1px)',
                    }
                  }}
                  onClick={() => handleChange('typeId', CertificateTypes.JAPAN)}
                  tabIndex={0}
                  role="button"
                  aria-label="Veldu Japan"
                  aria-pressed={certificateFormData?.typeId === CertificateTypes.JAPAN}
                  onKeyDown={e => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault();
                      handleChange('typeId', CertificateTypes.JAPAN);
                    }
                  }}
                >
                  <img
                    src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANBAMAAABbflNtAAAAG1BMVEX///+8AC3EH0bJM1fXaIL66+7hj6Ltusa9BzJnmT8QAAAAL0lEQVQI12NgwAnK0mGsYEFBUyjTUVBQBMpUFBQUgjIbBQUlMEWR1CKZgGQuJgAAgvAFYoodbs4AAAAASUVORK5CYII="
                    width="22"
                    alt="Japan"
                    style={{ marginRight: '0.5em', filter: 'grayscale(10%)' }}
                  />
                  <span style={{ flex: 1, lineHeight: 1.15, fontSize: '0.95rem' }}>Japan / Annað</span>
                  {certificateFormData?.typeId === CertificateTypes.JAPAN && (
                    <CheckIcon sx={{ ml: 1, color: 'primary.main', fontSize: 18 }} />
                  )}
                </Box>
              </Box>
            </InputRow>

            <InputRow>
              <TextField
                onChange={(e) => {
                  handleChange("referenceNumber", e.target.value);
                  setErrorModel({
                    ...errorModel, referenceNumber: {
                      error: e.target.value.length > REFERENCE_NUMBER_MAX_LENGTH ? true : false,
                      message: e.target.value.length > REFERENCE_NUMBER_MAX_LENGTH ? 'Tilvísunarnúmer má ekki vera lengra en 20 stafir.' : '',
                    }
                  });
                }}
                value={
                  certificateFormData?.referenceNumber
                    ? certificateFormData?.referenceNumber
                    : ""
                }
                sx={{
                  width: '100%'
                }}
                label="Tilvísunarnúmer"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ContentPasteIcon />
                    </InputAdornment>
                  ),
                }}
                autoFocus={props.inputToFocus === 'referenceNumber' ? true : false}
                autoComplete='off'
                error={errorModel?.referenceNumber?.error}
                helperText={errorModel?.referenceNumber?.message}
              />

            </InputRow>

            <Box sx={{ mb: 1.25 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                Flutningsupplýsingar
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Veldu flutningsmáta og skráðu upplýsingar sem eiga að birtast á vottorðinu.
              </Typography>
            </Box>
            <InputRow>
              <Box sx={{
                display: 'flex',
                gap: 1,
                width: '100%',
                justifyContent: 'stretch',
                mt: 0.5,
                mb: 0.5,
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
              }}>
                {/* Flutningaskip */}
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.2,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    boxShadow: transportMethod === TransportMethods.SHIP ? '0 1px 4px rgba(25,118,210,0.10)' : 'none',
                    background:
                      transportMethod === TransportMethods.SHIP
                        ? 'linear-gradient(180deg, #f5faff 0%, #f0f7ff 100%)'
                        : 'background.paper',
                    fontWeight: transportMethod === TransportMethods.SHIP ? 600 : 500,
                    fontSize: '0.95rem',
                    minHeight: 40,
                    outline: 'none',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: '0 1px 6px rgba(25,118,210,0.13)',
                      transform: 'translateY(-1px)',
                    },
                    '&:focus-visible': {
                      borderColor: 'primary.main',
                      boxShadow: '0 1px 6px rgba(25,118,210,0.13)',
                      transform: 'translateY(-1px)',
                    }
                  }}
                  onClick={() => handleSelectTransportMethod(TransportMethods.SHIP)}
                  onKeyDown={e => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault();
                      handleSelectTransportMethod(TransportMethods.SHIP);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label="Veldu flutningaskip"
                  aria-pressed={transportMethod === TransportMethods.SHIP}
                >
                  <DirectionsBoatFilledIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                  <span style={{ flex: 1, lineHeight: 1.15, fontSize: '0.95rem' }}>Flutningaskip</span>
                  {transportMethod === TransportMethods.SHIP && (
                    <CheckIcon sx={{ ml: 1, color: 'primary.main', fontSize: 18 }} />
                  )}
                </Box>
                {/* Flugvél */}
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.2,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    boxShadow: transportMethod === TransportMethods.AIRPLANE ? '0 1px 4px rgba(25,118,210,0.10)' : 'none',
                    background:
                      transportMethod === TransportMethods.AIRPLANE
                        ? 'linear-gradient(180deg, #f5faff 0%, #f0f7ff 100%)'
                        : 'background.paper',
                    fontWeight: transportMethod === TransportMethods.AIRPLANE ? 600 : 500,
                    fontSize: '0.95rem',
                    minHeight: 40,
                    outline: 'none',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: '0 1px 6px rgba(25,118,210,0.13)',
                      transform: 'translateY(-1px)',
                    },
                    '&:focus-visible': {
                      borderColor: 'primary.main',
                      boxShadow: '0 1px 6px rgba(25,118,210,0.13)',
                      transform: 'translateY(-1px)',
                    }
                  }}
                  onClick={() => handleSelectTransportMethod(TransportMethods.AIRPLANE)}
                  onKeyDown={e => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault();
                      handleSelectTransportMethod(TransportMethods.AIRPLANE);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label="Veldu flugvél"
                  aria-pressed={transportMethod === TransportMethods.AIRPLANE}
                >
                  <FlightIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                  <span style={{ flex: 1, lineHeight: 1.15, fontSize: '0.95rem' }}>Flugvél</span>
                  {transportMethod === TransportMethods.AIRPLANE && (
                    <CheckIcon sx={{ ml: 1, color: 'primary.main', fontSize: 18 }} />
                  )}
                </Box>
              </Box>
            </InputRow>

            <InputRow>
              {transportMethod === TransportMethods.SHIP && (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.75,
                    p: { xs: 1.5, sm: 2 },
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'transparent',
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1.7fr) minmax(0, 0.8fr) minmax(0, 1fr)' },
                      gap: 1.25,
                      alignItems: 'start',
                    }}
                  >
                    <TextField
                      onChange={(e) => {
                        handleChange("transportingShip", e.target.value);
                      }}
                      value={
                        certificateFormData?.transportingShip
                          ? certificateFormData?.transportingShip
                          : ""
                      }
                      sx={{ width: '100%' }}
                      label="Nafn flutningaskips"
                      required={certificateFormData?.typeId === CertificateTypes.EU}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <DirectionsBoatFilledIcon />
                          </InputAdornment>
                        ),
                      }}
                      autoFocus={props.inputToFocus === 'transportingShip' ? true : false}
                      autoComplete='off'
                      error={errorModel?.transportingShip?.error}
                      helperText={errorModel?.transportingShip?.message}
                    />
                    <TextField
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 7);
                        handleChange("transportingShipImoNumber", value);
                      }}
                      value={certificateFormData?.transportingShipImoNumber ?? ""}
                      label="IMO-númer"
                      placeholder="9333383"
                      inputProps={{ inputMode: 'numeric', maxLength: 7 }}
                      autoFocus={props.inputToFocus === 'transportingShipImoNumber'}
                      autoComplete="off"
                      error={errorModel?.transportingShipImoNumber?.error}
                      helperText={errorModel?.transportingShipImoNumber?.message || 'Valfrjálst · 7 tölustafir'}
                    />
                    <TextField
                      onChange={(e) => handleChange("voyageNumber", e.target.value)}
                      value={certificateFormData?.voyageNumber ?? ""}
                      label="Ferðanúmer"
                      placeholder="JOR-2632"
                      inputProps={{ maxLength: VOYAGE_NUMBER_MAX_LENGTH }}
                      autoFocus={props.inputToFocus === 'voyageNumber'}
                      autoComplete="off"
                      error={errorModel?.voyageNumber?.error}
                      helperText={errorModel?.voyageNumber?.message || 'Valfrjálst · t.d. JOR-2632'}
                    />
                  </Box>
                  <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
                    <Button
                      type="button"
                      size="small"
                      color={showRoadVehicleNumber ? 'primary' : 'inherit'}
                      startIcon={<LocalShippingIcon />}
                      onClick={() => {
                        if (showRoadVehicleNumber) {
                          setShowRoadVehicleNumber(false);
                          handleChange("roadVehicleNumber", null);
                        } else {
                          setShowRoadVehicleNumber(true);
                        }
                      }}
                      sx={{ px: 0.5, fontWeight: 600, textTransform: 'none' }}
                    >
                      {showRoadVehicleNumber ? 'Fjarlægja vagnnúmer' : 'Bæta við vagnnúmeri'}
                    </Button>
                  </Box>
                  <Collapse in={showRoadVehicleNumber} timeout={180} unmountOnExit>
                    <Box sx={{ mt: -0.75 }}>
                      <TextField
                        onChange={(e) => {
                          handleChange("roadVehicleNumber", e.target.value);
                        }}
                        value={certificateFormData?.roadVehicleNumber ?? ""}
                        sx={{ width: '100%' }}
                        label="Bílnúmer / vagnnúmer"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocalShippingIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                            </InputAdornment>
                          ),
                          inputProps: { maxLength: ROAD_VEHICLE_NUMBER_MAX_LENGTH },
                        }}
                        autoFocus={props.inputToFocus === 'roadVehicleNumber' ? true : false}
                        autoComplete='off'
                      />
                    </Box>
                  </Collapse>
                </Box>)
              }
              {transportMethod === TransportMethods.AIRPLANE && (
                <Box
                  sx={{
                    width: '100%',
                    p: { xs: 1.5, sm: 2 },
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'transparent',
                  }}
                >
                  <TextField
                    onChange={(e) => {
                      handleChange("flightNumber", e.target.value);
                    }}
                    value={certificateFormData?.flightNumber || ""}
                    sx={{ width: '100%' }}
                    label="Flugnúmer"
                    required={certificateFormData?.typeId === CertificateTypes.EU}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FlightIcon />
                        </InputAdornment>
                      ),
                    }}
                    autoFocus={props.inputToFocus === 'flightNumber'}
                    error={errorModel?.flightNumber?.error}
                    helperText={errorModel?.flightNumber?.message}
                  />
                </Box>)
              }
            </InputRow>

            {transportMethod === TransportMethods.SHIP && (
              <InputRow>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    width: '100%',
                    justifyContent: 'stretch',
                    mt: 0.25,
                    mb: 0.25,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  }}
                >
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1.1,
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      boxShadow: containerMode === 'containers' ? '0 1px 4px rgba(25,118,210,0.10)' : 'none',
                      background:
                        containerMode === 'containers'
                          ? 'linear-gradient(180deg, #f5faff 0%, #f0f7ff 100%)'
                          : 'background.paper',
                      fontWeight: containerMode === 'containers' ? 600 : 500,
                      fontSize: '0.92rem',
                      minHeight: 40,
                      outline: 'none',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: '0 1px 6px rgba(25,118,210,0.13)',
                        transform: 'translateY(-1px)',
                      },
                      '&:focus-visible': {
                        borderColor: 'primary.main',
                        boxShadow: '0 1px 6px rgba(25,118,210,0.13)',
                        transform: 'translateY(-1px)',
                      }
                    }}
                    onClick={() => handleSelectContainerMode('containers')}
                    onKeyDown={e => {
                      if (e.key === ' ' || e.key === 'Enter') {
                        e.preventDefault();
                        handleSelectContainerMode('containers');
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label="Veldu gámaflutning"
                    aria-pressed={containerMode === 'containers'}
                  >
                    <GridOnIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                    <span style={{ flex: 1, lineHeight: 1.15 }}>Gámar</span>
                    {containerMode === 'containers' && (
                      <CheckIcon sx={{ ml: 1, color: 'primary.main', fontSize: 18 }} />
                    )}
                  </Box>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1.1,
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      boxShadow: containerMode === 'no-containers' ? '0 1px 4px rgba(25,118,210,0.10)' : 'none',
                      background:
                        containerMode === 'no-containers'
                          ? 'linear-gradient(180deg, #fffaf2 0%, #fff6e5 100%)'
                          : 'background.paper',
                      fontWeight: containerMode === 'no-containers' ? 600 : 500,
                      fontSize: '0.92rem',
                      minHeight: 40,
                      outline: 'none',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        borderColor: 'warning.main',
                        boxShadow: '0 1px 6px rgba(245,158,11,0.18)',
                        transform: 'translateY(-1px)',
                      },
                      '&:focus-visible': {
                        borderColor: 'warning.main',
                        boxShadow: '0 1px 6px rgba(245,158,11,0.18)',
                        transform: 'translateY(-1px)',
                      }
                    }}
                    onClick={() => handleSelectContainerMode('no-containers')}
                    onKeyDown={e => {
                      if (e.key === ' ' || e.key === 'Enter') {
                        e.preventDefault();
                        handleSelectContainerMode('no-containers');
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label="Veldu engir gámar"
                    aria-pressed={containerMode === 'no-containers'}
                  >
                    <BlockIcon sx={{ mr: 1, color: 'warning.main', fontSize: 20 }} />
                    <span style={{ flex: 1, lineHeight: 1.15 }}>Engir gámar</span>
                    {containerMode === 'no-containers' && (
                      <CheckIcon sx={{ ml: 1, color: 'warning.main', fontSize: 18 }} />
                    )}
                  </Box>
                </Box>
              </InputRow>
            )}

            {transportMethod === TransportMethods.SHIP && containerMode === 'containers' && (
              <InputRow>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
                    <TextField
                      onChange={(e) => handleContainerInputChange(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (canAddContainers) {
                            addContainersFromInput();
                          } else {
                            setErrorModel(prev => ({
                              ...prev,
                              containerNumber: {
                                error: true,
                                message: `${CONTAINER_FORMAT_HELP} Ýttu á Enter eða "Bæta við" til að bæta við.`
                              }
                            }));
                          }
                        }
                      }}
                      value={containerInput}
                      sx={{ width: '100%' }}
                      label="Gámanúmer"
                      required
                      placeholder={requiresContainerRegex ? "AAAU1234567" : ""}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <GridOnIcon />
                          </InputAdornment>
                        ),
                      }}
                      autoFocus={props.inputToFocus === 'containerNumber' ? true : false}
                      autoComplete='off'
                      error={errorModel?.containerNumber?.error}
                      helperText={
                        errorModel?.containerNumber?.error
                          ? errorModel?.containerNumber?.message
                          : containerMode === 'containers' && containerList.length === 0
                            ? 'Skráðu að minnsta kosti eitt gámanúmer.'
                            : requiresContainerRegex
                              ? CONTAINER_FORMAT_HELP
                              : ''
                      }
                    />
                    <Button
                      variant="outlined"
                      size="medium"
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={() => {
                        if (canAddContainers) {
                          addContainersFromInput();
                        } else {
                          setErrorModel(prev => ({
                            ...prev,
                            containerNumber: {
                              error: true,
                              message: `${CONTAINER_FORMAT_HELP} Lagfærðu áður en þú bætir við.`
                            }
                          }));
                        }
                      }}
                      disabled={!canAddContainers}
                      sx={{
                        alignSelf: 'flex-start',
                        minWidth: 140,
                        height: '4em',
                        boxShadow: 'none',
                        px: 2.4
                      }}
                    >
                      Bæta við
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: 'rgba(25,118,210,0.03)',
                      p: 1.25,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <GridOnIcon sx={{ color: 'primary.main', fontSize: 19 }} />
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
                        Skráðir gámar
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        Innsiglisnúmer eru valkvæð.
                      </Typography>
                    </Box>
                    {containerList.map((container) => {
                      const isValid = requiresContainerRegex
                        ? CONTAINER_REGEX.test(container)
                        : true;
                      return (
                        <Box
                          key={container}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'minmax(180px, 240px) 1fr' },
                            gap: 1,
                            alignItems: 'center'
                          }}
                        >
                          <Chip
                            icon={<GridOnIcon />}
                            label={container}
                            onDelete={() => handleRemoveContainer(container)}
                            color={isValid ? 'success' : 'error'}
                            variant="outlined"
                            sx={{
                              justifyContent: 'space-between',
                              height: 40,
                              borderStyle: isValid ? 'solid' : 'dashed',
                              borderColor: isValid ? 'success.light' : 'error.light',
                              backgroundColor: isValid ? 'rgba(46,125,50,0.10)' : 'rgba(211,47,47,0.08)',
                              color: isValid ? 'success.dark' : 'error.dark',
                              fontWeight: 600
                            }}
                          />
                          <TextField
                            size="small"
                            label="Innsiglisnúmer"
                            value={containerSealMap[container] || ''}
                            onChange={(e) => handleContainerSealChange(container, e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <VpnKeyIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                            inputProps={{ maxLength: 100 }}
                            helperText="Valkvætt"
                            autoComplete="off"
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </InputRow>
            )}

            {transportMethod === TransportMethods.SHIP && containerMode === 'no-containers' && (
              <InputRow>
                <Box
                  sx={{
                    width: '100%',
                    border: '1px solid',
                    borderColor: 'warning.light',
                    borderRadius: 1,
                    p: 1.2,
                    backgroundColor: 'rgba(245,158,11,0.08)',
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'warning.dark', fontWeight: 600 }}>
                    Með þessu staðfestir þú að sendingin hafi ekki verið flutt í gámum.
                  </Typography>
                </Box>
              </InputRow>
            )}

            <InputRow>
              <TextField
                onChange={(e) => {
                  handleChange("trackingNumber", e.target.value)
                  setErrorModel({
                    ...errorModel, trackingNumber: {
                      error: e.target.value.length > TRACKING_NUMBER_MAX_LENGTH ? true : false,
                      message: e.target.value.length > TRACKING_NUMBER_MAX_LENGTH ? 'Farmbréfsnúmer má ekki vera lengra en 100 stafir.' : '',
                    }
                  });
                }
                }
                value={
                  certificateFormData?.trackingNumber
                    ? certificateFormData?.trackingNumber
                    : ""
                }
                sx={{ width: '100%' }}
                label="Farmbréfsnúmer"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ManageSearchIcon />
                    </InputAdornment>
                  ),
                }}
                autoFocus={props.inputToFocus === 'trackingNumber' ? true : false}
                error={errorModel?.trackingNumber?.error}
                helperText={errorModel?.trackingNumber?.message}
              />
            </InputRow>

            <Box>
              <FormLabel
                sx={{
                  fontSize: 14,
                  color: 'text.primary',
                  fontWeight: 600,
                  letterSpacing: 0.2,
                  borderBottom: '2px solid #1976d2',
                  pb: 0.5,
                  mb: 1,
                  display: 'inline-block'
                }}
              >
                Aðilaupplýsingar
              </FormLabel>
            </Box>

            <InputRow
              style={{ gap: 0 }}
            >
              {/* Receiver Info Box */}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  p: 2,
                  mr: 0.5,
                  minWidth: 0,
                  background: '#fafafa',
                  alignItems: 'flex-start',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s',
                  boxShadow: 'none',
                  outline: 'none', // Prevent default focus outline
                  '&:hover': {
                    boxShadow: '0 0 0 1px #1976d2',
                  },
                  '&:focus': {
                    outline: 'none',
                    borderColor: 'primary.main',
                    boxShadow: '0 1px 6px rgba(25,118,210,0.13)',
                    transform: 'translateY(-1px)',
                  },
                }}
                onClick={() => setReceiverModalOpen(true)}
                onKeyDown={e => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    setReceiverModalOpen(true);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label="Breyta móttakanda"
              >
                <Box sx={{
                  position: 'absolute', top: 8, right: 8
                }}>
                  <Tooltip title="Breyta móttakanda" arrow>
                    <Button
                      size="small"
                      sx={{ minWidth: 0, p: 0.5 }}
                      tabIndex={-1}
                      onClick={e => {
                        e.stopPropagation();
                        setReceiverModalOpen(true);
                      }}
                    >
                      <Edit color="action" fontSize="small" />
                    </Button>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <BusinessIcon color="action" sx={{ mr: 1 }} />
                  <Box sx={{ fontWeight: 600, fontSize: '1rem', color: 'black' }}>Móttakandi</Box>
                </Box>
                <Box sx={{ fontWeight: 300, whiteSpace: 'pre-line' }}>
                  {(certificateFormData?.receiverName &&
                    certificateFormData?.receiverAddress &&
                    certificateFormData?.receiverCountryCodeId &&
                    countryCodes.find(
                      (country) => country.id === certificateFormData?.receiverCountryCodeId
                    )) ? (
                    <>
                      {certificateFormData.receiverName}
                      {`\n${certificateFormData.receiverAddress}`}
                      {certificateFormData.receiverCity ? `\n${certificateFormData.receiverCity}` : ""}
                      {certificateFormData.receiverPostalCode ? `\n${certificateFormData.receiverPostalCode}` : ""}
                      {`\n${countryCodes.find(
                        (country) => country.id === certificateFormData?.receiverCountryCodeId
                      )?.countryCode} - ${countryCodes.find(
                        (country) => country.id === certificateFormData?.receiverCountryCodeId
                      )?.countryName}`}
                      {certificateFormData.receiverEORI ? `\nEORI: ${certificateFormData.receiverEORI}` : ""}
                    </>
                  ) : (
                    <span style={{ color: '#bbb' }}>Ekki skráð</span>
                  )
                  }
                </Box>
              </Box>

              <Modal
                open={receiverModalOpen}
                onClose={() => setReceiverModalOpen(false)}
              >
                <StyledBox>
                  <Box>
                    <FormLabel
                      sx={{
                        fontSize: 14,
                        color: 'text.primary',
                        fontWeight: 600,
                        letterSpacing: 0.2,
                        borderBottom: '2px solid #1976d2',
                        pb: 0.5,
                        mb: 1,
                        display: 'inline-block'
                      }}
                    >
                      Móttakandi
                    </FormLabel>
                  </Box>
                  <InputFieldContainer>
                    <InputRow>
                      <Autocomplete
                        onChange={(e, value) => {
                          if (typeof value !== 'string') {
                            handleChange("receiverName", value?.subjectName);
                            handleChange("receiverAddress", value?.streetName);
                            handleChange("receiverCity", value?.cityName);
                            handleChange("receiverPostalCode", value?.postalCode);
                            handleChange("receiverCountryCodeId", props.referenceData?.countryCodes?.find(country => country.countryCode === value?.countryCode)?.id || null);
                            handleChange("receiverEORI", value?.eori);
                            setErrorModel({
                              ...errorModel, receiverName: {
                                error: value?.subjectName && value.subjectName?.length > RECEIVER_NAME_MAX_LENGTH ? true : false,
                                message: value?.subjectName && value?.subjectName?.length > RECEIVER_NAME_MAX_LENGTH ? 'Nafn móttakanda má ekki vera lengra en 50 stafir.' : '',
                              }
                            });
                            setErrorModel({
                              ...errorModel, receiverAddress: {
                                error: value?.streetName && value.streetName?.length > RECEIVER_ADDRESS_MAX_LENGTH ? true : false,
                                message: value?.streetName && value?.streetName?.length > RECEIVER_ADDRESS_MAX_LENGTH ? 'Heimilisfang móttakanda má ekki vera lengra en 500 stafir.' : '',
                              }
                            });
                          }
                        }}
                        onInputChange={(event, newInputValue) => {
                          if (typeof newInputValue === 'string') {
                            handleChange("receiverName", newInputValue);
                            setErrorModel({
                              ...errorModel, receiverName: {
                                error: newInputValue?.length > RECEIVER_NAME_MAX_LENGTH ? true : false,
                                message: newInputValue?.length > RECEIVER_NAME_MAX_LENGTH ? 'Nafn móttakanda má ekki vera lengra en 50 stafir.' : '',
                              }
                            });
                          }
                        }}
                        value={
                          certificateFormData?.receiverName
                            ? certificateFormData?.receiverName
                            : ""
                        }
                        disablePortal={false}
                        options={props.receiverAddresses}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') {
                            return option ? option : '';
                          } else {
                            return option.subjectName ? option.subjectName : '';
                          }
                        }}
                        renderOption={(props, option) => {
                          return (
                            <li {...props} key={props.id}>
                              {option.subjectName}
                            </li>
                          );
                        }}
                        sx={{ width: '100%' }}
                        clearOnBlur={false}
                        freeSolo
                        renderInput={(params) => <TextField
                          label="Nafn móttakanda"
                          {...params}
                          required
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position='start'>
                                <BusinessIcon />
                              </InputAdornment>
                            )
                          }}
                          autoFocus={props.inputToFocus === 'receiverName' ? true : false}
                          error={errorModel?.receiverName?.error}
                          helperText={errorModel?.receiverName?.message}
                        />
                        }
                      />
                    </InputRow>

                    <InputRow>
                      <Autocomplete
                        onChange={(e, value) => {
                          if (typeof value !== 'string') {
                            handleChange("receiverAddress", value?.streetName);
                            setErrorModel({
                              ...errorModel, receiverAddress: {
                                error: value?.streetName && value.streetName?.length > RECEIVER_ADDRESS_MAX_LENGTH ? true : false,
                                message: value?.streetName && value?.streetName?.length > RECEIVER_ADDRESS_MAX_LENGTH ? 'Heimilisfang móttakanda má ekki vera lengra en 500 stafir.' : '',
                              }
                            });
                          }
                        }}
                        onInputChange={(event, newInputValue) => {
                          if (typeof newInputValue === 'string') {
                            handleChange("receiverAddress", newInputValue);
                            setErrorModel({
                              ...errorModel, receiverAddress: {
                                error: newInputValue?.length > RECEIVER_ADDRESS_MAX_LENGTH ? true : false,
                                message: newInputValue?.length > RECEIVER_ADDRESS_MAX_LENGTH ? 'Heimilisfang móttakanda má ekki vera lengra en 500 stafir.' : '',
                              }
                            });
                          }
                        }}
                        value={
                          certificateFormData?.receiverAddress
                            ? certificateFormData?.receiverAddress
                            : ""
                        }
                        disablePortal={false}
                        options={props.receiverAddresses}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') {
                            return option ? option : '';
                          } else {
                            return option.streetName ? option.streetName : '';
                          }
                        }}
                        renderOption={(props, option) => {
                          return (
                            <li {...props} key={props.id}>
                              {option.streetName}
                            </li>
                          );
                        }}
                        sx={{ width: '100%' }}
                        clearOnBlur={false}
                        freeSolo
                        renderInput={(params) => <TextField
                          multiline
                          rows={2}
                          key={params.id}
                          required
                          label="Heimilisfang móttakanda"
                          {...params}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position='start'>
                                <BusinessIcon />
                              </InputAdornment>
                            )
                          }}
                          error={errorModel?.receiverAddress?.error}
                          helperText={errorModel?.receiverAddress?.message}
                        />
                        }
                      />
                    </InputRow>

                    {/* City and Postal Code fields for receiver */}
                    <InputRow>
                      <TextField
                        label="Borg"
                        required
                        value={certificateFormData?.receiverCity || ""}
                        onChange={e => handleChange("receiverCity", e.target.value)}
                        sx={{ width: '100%' }}
                        autoComplete="off"
                        error={errorModel?.receiverCity?.error}
                        helperText={errorModel?.receiverCity?.message}
                      />
                    </InputRow>
                    <InputRow>
                      <TextField
                        label="Póstnúmer"
                        required
                        value={certificateFormData?.receiverPostalCode || ""}
                        onChange={e => handleChange("receiverPostalCode", e.target.value)}
                        sx={{ width: '100%' }}
                        autoComplete="off"
                        error={errorModel?.receiverPostalCode?.error}
                        helperText={errorModel?.receiverPostalCode?.message}
                      />
                    </InputRow>

                    <InputRow>
                      <Autocomplete
                        options={receiverCountryOptions}
                        getOptionLabel={(option) =>
                          typeof option === 'string'
                            ? option
                            : `${option.countryCode} - ${option.countryName}`
                        }
                        value={
                          receiverCountryOptions.find(
                            (country) => country.id === certificateFormData?.receiverCountryCodeId
                          ) || null
                        }
                        onChange={(event, value) => {
                          handleChange("receiverCountryCodeId", value ? value.id : null);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Landskóði móttakanda"
                            required
                          />
                        )}
                        sx={{ width: '100%' }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                      />
                    </InputRow>

                    {/* Receiver EORI */}
                    <InputRow style={{ paddingBottom: 0 }}>
                      <Accordion
                        elevation={0}
                        disableGutters
                        expanded={showReceiverEORI}
                        onChange={(_, expanded) => setShowReceiverEORI(expanded)}
                        sx={{
                          width: '100%',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          backgroundColor: '#fafafa',
                          overflow: 'hidden',
                          '&:before': { display: 'none' }, // remove default divider line
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                          px: 2,
                          py: 0.5,
                          '& .MuiAccordionSummary-content': { my: 1 },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            EORI númer móttakanda
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                            Valfrjálst — fyllið aðeins út ef þið vitið EORI númer móttakanda
                            </Typography>
                          </Box>
                          <Tooltip 
                            title="Þarf að passa við EORI númer móttakanda í Traces kerfinu"
                            arrow
                          >
                            <HelpOutlineIcon 
                            sx={{ 
                              fontSize: 18, 
                              color: 'text.secondary',
                              cursor: 'help'
                            }} 
                            />
                          </Tooltip>
                          </Box>
                        </AccordionSummary>

                        <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                          <TextField
                            label="EORI númer móttakanda"
                            value={certificateFormData?.receiverEORI || ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              handleChange('receiverEORI', val);

                              const trimmed = val.trim();
                              if (trimmed.length === 0) {
                                setErrorModel((prev) => ({
                                  ...prev,
                                  receiverEORI: { error: false, message: '' },
                                }));
                              } else {
                                const normalized = normalizeEori(val);
                                const valid = looksLikeEori(normalized);
                                setErrorModel((prev) => ({
                                  ...prev,
                                  receiverEORI: {
                                    error: !valid,
                                    message: !valid
                                      ? 'Ógilt EORI snið. Fyllið þetta aðeins út ef þið vitið EORI númer móttakanda.'
                                      : '',
                                  },
                                }));
                              }
                            }}
                            fullWidth
                            autoComplete="off"
                            error={errorModel?.receiverEORI?.error}
                            helperText={errorModel?.receiverEORI?.message}
                            placeholder="t.d. IS1234567890"
                            size="small"
                          />
                        </AccordionDetails>
                      </Accordion>
                    </InputRow>

                  </InputFieldContainer>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button variant="contained" onClick={() => setReceiverModalOpen(false)}>
                      Loka
                    </Button>
                  </Box>
                </StyledBox>
              </Modal>

              <Modal
                open={exporterModalOpen}
                onClose={() => setExporterModalOpen(false)}
              >
                <StyledBox>

                  <Box>
                    <FormLabel
                      sx={{
                        fontSize: 14,
                        color: 'text.primary',
                        fontWeight: 600,
                        letterSpacing: 0.2,
                        borderBottom: '2px solid #1976d2',
                        pb: 0.5,
                        mb: 1,
                        display: 'inline-block'
                      }}
                    >
                      Útflytjandi
                    </FormLabel>
                  </Box>
                  <InputFieldContainer>
                    <InputRow>
                      <Autocomplete
                        onChange={(e, value) => {
                          if (typeof value !== 'string') {
                            handleChange("exporterName", value?.subjectName);
                            setErrorModel({
                              ...errorModel, exporterName: {
                                error: value?.subjectName && value.subjectName?.length > EXPORTER_NAME_MAX_LENGTH ? true : false,
                                message: value?.subjectName && value?.subjectName?.length > EXPORTER_NAME_MAX_LENGTH ? 'Nafn útflytjanda má ekki vera lengra en 50 stafir.' : '',
                              }
                            });
                          }
                        }}
                        onInputChange={(event, newInputValue) => {
                          if (typeof newInputValue === 'string') {
                            handleChange("exporterName", newInputValue);
                            setErrorModel({
                              ...errorModel, exporterName: {
                                error: newInputValue?.length > EXPORTER_NAME_MAX_LENGTH ? true : false,
                                message: newInputValue?.length > EXPORTER_NAME_MAX_LENGTH ? 'Nafn útflytjanda má ekki vera lengra en 50 stafir.' : '',
                              }
                            });
                          }
                        }}
                        defaultValue={props.exporterAddresses.length > 0 ? props.exporterAddresses[0] : null}
                        value={
                          props.userInfo?.isAdmin
                            ? certificateFormData?.exporterName || currentUserCompanyName || ""
                            : certificateFormData?.exporterName || ""
                        }
                        disablePortal={false}
                        options={props.exporterAddresses}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') {
                            return option ? option : '';
                          } else {
                            return option.subjectName ? option.subjectName : '';
                          }
                        }}
                        renderOption={(props, option) => {
                          return (
                            <li {...props} key={props.id}>
                              {option.subjectName}
                            </li>
                          );
                        }}
                        sx={{ width: '100%' }}
                        clearOnBlur={false}
                        freeSolo
                        renderInput={(params) => <TextField
                          label="Nafn útflytjanda"
                          required
                          {...params}
                          error={errorModel?.exporterName?.error}
                          helperText={errorModel?.exporterName?.message}
                        />
                        }
                      />
                    </InputRow>

                    <InputRow>
                      <Autocomplete
                        onChange={(e, value) => {
                          if (typeof value !== 'string') {
                            handleChange("exporterAddress", value?.streetName);
                            setErrorModel({
                              ...errorModel, exporterAddress: {
                                error: value?.streetName && value.streetName?.length > EXPORTER_ADDRESS_MAX_LENGTH ? true : false,
                                message: value?.streetName && value?.streetName?.length > EXPORTER_ADDRESS_MAX_LENGTH ? 'Heimilisfang útflytjanda má ekki vera lengra en 500 stafir.' : '',
                              }
                            });
                          }
                        }}
                        onInputChange={(event, newInputValue) => {
                          if (typeof newInputValue === 'string') {
                            handleChange("exporterAddress", newInputValue);
                            setErrorModel({
                              ...errorModel, exporterAddress: {
                                error: newInputValue?.length > EXPORTER_ADDRESS_MAX_LENGTH ? true : false,
                                message: newInputValue?.length > EXPORTER_ADDRESS_MAX_LENGTH ? 'Heimilisfang útflytjanda má ekki vera lengra en 500 stafir.' : '',
                              }
                            });
                          }
                        }}
                        value={
                          certificateFormData?.exporterAddress
                            ? certificateFormData?.exporterAddress
                            : ""
                        }
                        disablePortal={false}
                        defaultValue={props.exporterAddresses.length > 0 ? props.exporterAddresses[0] : null}
                        options={props.exporterAddresses}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') {
                            return option ? option : '';
                          } else {
                            return option.streetName ? option.streetName : '';
                          }
                        }}
                        renderOption={(props, option) => {
                          return (
                            <li {...props} key={props.id}>
                              {option.streetName}
                            </li>
                          );
                        }}
                        sx={{ width: '100%' }}
                        clearOnBlur={false}
                        freeSolo
                        renderInput={(params) => <TextField
                          multiline
                          tabIndex={0}
                          rows={2}
                          label="Heimilisfang útflytjanda"
                          required
                          {...params}
                          InputProps={{
                            ...params.InputProps,
                            inputProps: { ...params.inputProps, tabIndex: -1 },
                            startAdornment: (
                              <InputAdornment position='start'>
                                <BusinessIcon />
                              </InputAdornment>
                            )
                          }}
                          autoFocus={props.inputToFocus === 'exporterAddress' ? true : false}
                          error={errorModel?.exporterAddress?.error}
                          helperText={errorModel?.exporterAddress?.message}
                        />
                        }
                      />
                    </InputRow>

                    {/* City and Postal Code fields for exporter */}
                    <InputRow>
                      <TextField
                        label="Borg"
                        required
                        value={certificateFormData?.exporterCity || ""}
                        onChange={e => handleChange("exporterCity", e.target.value)}
                        sx={{ width: '100%' }}
                        autoComplete="off"
                        error={errorModel?.exporterCity?.error}
                        helperText={errorModel?.exporterCity?.message}
                      />
                    </InputRow>
                    <InputRow>
                      <TextField
                        label="Póstnúmer"
                        required
                        value={certificateFormData?.exporterPostalCode || ""}
                        onChange={e => handleChange("exporterPostalCode", e.target.value)}
                        sx={{ width: '100%' }}
                        autoComplete="off"
                        error={errorModel?.exporterPostalCode?.error}
                        helperText={errorModel?.exporterPostalCode?.message}
                      />
                    </InputRow>

                    <InputRow>
                      <TextField
                        label="Landskóði útflytjanda"
                        required
                        value={icelandCountry ? `${icelandCountry.countryCode} - ${icelandCountry.countryName}` : ''}
                        sx={{ width: '100%' }}
                        InputProps={{ readOnly: true }}
                      />
                    </InputRow>

                    {/* EORI number field for exporter - Commented out for now as I don't know if this is neccecary */}
                    {/*
                    <InputRow style={{ paddingBottom: 0 }}>
                      <Accordion
                      elevation={0}
                      disableGutters
                      expanded={showExporterEORI}
                      onChange={(_, expanded) => setShowExporterEORI(expanded)}
                      sx={{
                        width: '100%',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        backgroundColor: '#fafafa',
                        overflow: 'hidden',
                        '&:before': { display: 'none' }, // remove default divider line
                      }}
                      >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                        px: 2,
                        py: 0.5,
                        '& .MuiAccordionSummary-content': { my: 1 },
                        }}
                      >
                        <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          EORI númer útflytjanda
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Valfrjálst — fyllið aðeins út ef þið vitið EORI númer útflytjanda.
                        </Typography>
                        </Box>
                      </AccordionSummary>

                      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                        <TextField
                        label="EORI númer útflytjanda"
                        value={certificateFormData?.exporterEORI || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          handleChange('exporterEORI', val);

                          const trimmed = val.trim();
                          if (trimmed.length === 0) {
                          setErrorModel((prev) => ({
                            ...prev,
                            exporterEORI: { error: false, message: '' },
                          }));
                          } else {
                          const normalized = normalizeEori(val);
                          const valid = looksLikeEori(normalized);
                          setErrorModel((prev) => ({
                            ...prev,
                            exporterEORI: {
                            error: !valid,
                            message: !valid
                              ? 'Ógilt EORI snið. Fyllið þetta aðeins út ef þið vitið EORI númer útflytjanda.'
                              : '',
                            },
                          }));
                          }
                        }}
                        fullWidth
                        autoComplete="off"
                        error={errorModel?.exporterEORI?.error}
                        helperText={errorModel?.exporterEORI?.message}
                        placeholder="t.d. IS1234567890"
                        size="small"
                        />
                      </AccordionDetails>
                      </Accordion>
                    </InputRow> */}

                  </InputFieldContainer>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button variant="contained" onClick={() => setExporterModalOpen(false)}>
                      Loka
                    </Button>
                  </Box>
                </StyledBox>
              </Modal>

              {/* Exporter Info Box */}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  p: 2,
                  ml: 0.5,
                  minWidth: 0,
                  background: '#fafafa',
                  alignItems: 'flex-start',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: '0 0 0 1px #1976d2',
                  },
                  '&:focus': {
                    outline: 'none',
                    borderColor: 'primary.main',
                    boxShadow: '0 1px 6px rgba(25,118,210,0.13)',
                    transform: 'translateY(-1px)',
                  },
                }}
                onClick={() => setExporterModalOpen(true)}
                onKeyDown={e => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    setExporterModalOpen(true);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label="Breyta útflytjanda"
              >
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  <Tooltip title="Breyta útflytjanda" arrow>
                    <Button
                      size="small"
                      sx={{ minWidth: 0, p: 0.5 }}
                      tabIndex={-1}
                      onClick={e => {
                        e.stopPropagation();
                        setExporterModalOpen(true);
                      }}
                    >
                      <Edit color="action" fontSize="small" />
                    </Button>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <BusinessIcon color="action" sx={{ mr: 1 }} />
                  <Box sx={{ fontWeight: 600, fontSize: '1rem', color: 'black' }}>Útflytjandi</Box>
                </Box>
                <Box sx={{ fontWeight: 300, whiteSpace: 'pre-line' }}>
                  {(certificateFormData?.exporterName &&
                    certificateFormData?.exporterAddress &&
                    certificateFormData?.exporterCountryCodeId &&
                    filteredCountryCodes.find(
                      (country) => country.id === certificateFormData?.exporterCountryCodeId
                    )) ? (
                    <>
                      {certificateFormData.exporterName}
                      {`\n${certificateFormData.exporterAddress}`}
                      {certificateFormData.exporterCity ? `\n${certificateFormData.exporterCity}` : ""}
                      {certificateFormData.exporterPostalCode ? `\n${certificateFormData.exporterPostalCode}` : ""}
                      {`\n${filteredCountryCodes.find(
                        (country) => country.id === certificateFormData?.exporterCountryCodeId
                      )?.countryCode} - ${filteredCountryCodes.find(
                        (country) => country.id === certificateFormData?.exporterCountryCodeId
                      )?.countryName}`}
                    </>
                  ) : (
                    <span style={{ color: '#bbb' }}>Ekki skráð</span>
                  )
                  }
                </Box>
              </Box>

            </InputRow>
          </InputFieldContainer>

          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'end', gap: 1, pt: 2, padding: 0 }}>
            <Button onClick={handleClose} disabled={isSaving}>Hætta við</Button>
            <Tooltip title={submitTooltip} arrow>
              <span>
                <Button
                  onClick={props.modalActionType === ActionTypes.EDIT_ACTION ? handleEditCertificate : handleSubmitCertificate}
                  variant="contained"
                  disabled={isSubmitDisabled}
                >
                  {isSaving ? 'Vista…' : props.modalActionType === ActionTypes.EDIT_ACTION ? 'Vista breytingar' : 'Búa til vottorð'}
                </Button>
              </span>
            </Tooltip>
          </Box>
          </Box>
        </StyledBox>
      </Modal>
      <UnsavedChangesDialog
        open={props.certificateModalOpen && discardDialogOpen}
        onCancel={() => setDiscardDialogOpen(false)}
        onDiscard={() => {
          setDiscardDialogOpen(false);
          props.toggleCertificateModal();
        }}
      />
    </CertificateModalWrapper >
  );
}

export default CertificateModal;
